import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { 
  TrendingUp, 
  Users, 
  ShoppingBag, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  BarChart3,
  LineChart
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminApi } from '../../utils/api';
import { toast } from 'react-hot-toast';

export const Dashboard = () => {
  const [chartType, setChartType] = useState<'sales' | 'orders'>('sales');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [statsResponse, ordersResponse, lowStockResponse] = await Promise.all([
          adminApi.dashboard.getStats(),
          adminApi.dashboard.getRecentOrders(),
          adminApi.dashboard.getLowStockProducts()
        ]);

        // Transform the data to match the expected structure
        const transformedStats = {
          totalRevenue: {
            title: 'Total Revenue',
            value: `KES ${statsResponse.data.totalSales.toLocaleString()}`,
            change: 12
          },
          totalOrders: {
            title: 'Total Orders',
            value: statsResponse.data.totalOrders,
            change: 8
          },
          totalCustomers: {
            title: 'Total Customers',
            value: 150,
            change: 5
          },
          conversionRate: {
            title: 'Conversion Rate',
            value: '2.4%',
            change: -2
          },
          chartData: [
            { name: 'Jan', sales: 4000 },
            { name: 'Feb', sales: 3000 },
            { name: 'Mar', sales: 5000 },
            { name: 'Apr', sales: 2780 },
            { name: 'May', sales: 1890 },
            { name: 'Jun', sales: 2390 }
          ],
          recentActivities: [
            { message: 'New order #1234 received', time: '2 minutes ago' },
            { message: 'Payment received for order #1233', time: '5 minutes ago' },
            { message: 'Order #1232 marked as shipped', time: '10 minutes ago' }
          ]
        };

        setStats(transformedStats);
        setRecentOrders(ordersResponse.data.map((order: any) => ({
          id: order.order_number,
          customer: order.customer_name,
          amount: parseFloat(order.total_amount),
          status: order.status,
          date: order.created_at
        })));
        setLowStockProducts(lowStockResponse.data);
      } catch (err: any) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to fetch dashboard data');
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-screen text-red-500">
          <p>{error}</p>
        </div>
      </AdminLayout>
    );
  }

  const orderStats = [
    { label: 'Pending Orders', value: stats?.pendingOrders || 0, icon: Clock, color: 'text-yellow-500' },
    { label: 'Processing Orders', value: stats?.processingOrders || 0, icon: TrendingUp, color: 'text-blue-500' },
    { label: 'Completed Orders', value: stats?.completedOrders || 0, icon: CheckCircle2, color: 'text-green-500' },
    { label: 'Cancelled Orders', value: stats?.cancelledOrders || 0, icon: XCircle, color: 'text-red-500' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-800">Dashboard</h2>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats && Object.entries(stats).map(([key, value]: [string, any]) => {
            const Icon = {
              totalRevenue: DollarSign,
              totalOrders: ShoppingBag,
              totalCustomers: Users,
              conversionRate: TrendingUp
            }[key] || TrendingUp;

            return (
              <div key={key} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="p-2 bg-pink-50 rounded-lg">
                    <Icon className="h-6 w-6 text-pink-500" />
                  </div>
                  <div className={`flex items-center gap-1 text-sm ${
                    value.change > 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {value.change > 0 ? '+' : ''}{value.change}%
                    {value.change > 0 ? (
                      <ArrowUpRight className="h-4 w-4" />
                    ) : (
                      <ArrowDownRight className="h-4 w-4" />
                    )}
                  </div>
                </div>
                <h3 className="text-gray-600 text-sm mt-4">{value.title}</h3>
                <p className="text-2xl font-semibold text-gray-800 mt-1">
                  {value.value}
                </p>
              </div>
            );
          })}
        </div>

        {/* Order Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {orderStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center gap-3">
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                  <div>
                    <p className="text-gray-600 text-sm">{stat.label}</p>
                    <p className="text-2xl font-semibold text-gray-800">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Orders */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">Recent Orders</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setChartType('sales')}
                  className={`p-2 rounded ${
                    chartType === 'sales' ? 'bg-pink-100 text-pink-600' : 'hover:bg-gray-100'
                  }`}
                >
                  <LineChart className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setChartType('orders')}
                  className={`p-2 rounded ${
                    chartType === 'orders' ? 'bg-pink-100 text-pink-600' : 'hover:bg-gray-100'
                  }`}
                >
                  <BarChart3 className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats?.chartData || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="sales"
                      stroke="#ec4899"
                      fill="#fce7f3"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-6">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-sm">
                      <th className="pb-3">Order ID</th>
                      <th className="pb-3">Customer</th>
                      <th className="pb-3">Amount</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {recentOrders.map((order) => (
                      <tr key={order.id}>
                        <td className="py-3">{order.id}</td>
                        <td className="py-3">{order.customer}</td>
                        <td className="py-3">KES {order.amount.toLocaleString()}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            order.status === 'completed' ? 'bg-green-100 text-green-600' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-600' :
                            order.status === 'processing' ? 'bg-blue-100 text-blue-600' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Logs */}
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-800">System Logs</h3>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    Low Stock Alerts
                  </h4>
                  <div className="space-y-3">
                    {lowStockProducts.map((product) => (
                      <div key={product.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{product.name}</span>
                        <span className="text-red-500">
                          {product.stock} left (min: {product.min_stock})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-3">Recent Activities</h4>
                  <div className="space-y-3">
                    {stats?.recentActivities?.map((activity: any, index: number) => (
                      <div key={index} className="text-sm text-gray-600">
                        {activity.message}
                        <span className="block text-xs text-gray-400">{activity.time}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loader CSS */}
      <style>{`
        .loader {
          border-top-color: #3498db;
          animation: spinner 1.5s linear infinite;
        }

        @keyframes spinner {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </AdminLayout>
  );
};